// helpers
import { useFetch, usePost, usePatch, useDelete, usePut } from "./reactQuery";
import API_ROUTES from "@/api/routes.constant";
import { pathToUrl } from "./reactQuery/pathToUrl";

// interfaces
import { IPaginatedResponse } from "@/types/api/PaginatedResponse";
import { ISingleSpaceResponse } from "@/types/api/SingleSpaceResponse";
import { QueryArgsInterface, IUseMutation } from "@/types/api/queryArgs";
import { IUseQueryResult } from "@/types/api/UseQueryResult";
import { ISpaceAvailableMonth, ISpaceFullResponse } from "@/types/Space";
import { ISpaceActivity, ILandlordSpaceActivity } from "@/types/api/SpaceActivity";
import { ISingleSpaceItem } from "@/types/api/SingleSpaceItem";
import { ICalculatePrice } from "@/types/api/CalculatePrice";
import { ISlot } from "@/types/api/Slot";
import { ISpaceBookingRequirements } from "@/types/api/SpaceBookingRequirements";
import { TActivityStatus } from "@/types/Global";
import {
  ISpaceFirstStepResponse,
  ISpaceSecondStepResponse,
  ISpaceThirdStepResponse,
  ISpaceFourthStepResponse,
  ISpaceFifthStepResponse,
  ISpaceSixthStepResponse,
} from "@/types/api/llp/ISpace";

type QueryBySlugArgs = {
  slug?: string;
  preview?: string;
} & QueryArgsInterface;

type TSpaceActivityQuery = {
  slug: string;
  activityId: string;
} & IUseMutation;


const spaces = {
  useSearchSpace: (args?: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch(API_ROUTES.spacesUrls.searchSpaces, params, config) as
    IUseQueryResult<IPaginatedResponse<ISingleSpaceResponse>>;
  },

  useSpace: (args?: QueryBySlugArgs) => {
    const { slug, preview, params, config = {} } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSingle, { slug }) : null;
    const fetchKeyPreview = slug ? pathToUrl(API_ROUTES.previewUrls.previewSpace, { slug }) : null;

    return useFetch(
      preview ? fetchKeyPreview : fetchKey,
      params,
      { ...config, axiosConfig: { headers: { "LOCATION-AUTH": `Bearer ${preview}` } } }
    ) as IUseQueryResult<ISpaceFullResponse>;
  },

  useSpaceActivities: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceActivities, { slug }) : null;

    return useFetch(fetchKey, params, config) as IUseQueryResult<ISpaceActivity[] | ILandlordSpaceActivity[]>;
  },
  useCalculateSpacePrice: (args?: IUseMutation<ICalculatePrice>) => {
    const { params, config } = args || {};

    return usePost<ICalculatePrice>(API_ROUTES.calculatePrice, params, config);
  },

  useSpaceBookingRequirements: (args?: IUseMutation<ISpaceBookingRequirements>) => {
    const { params, config, slug } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceBookingRequirements, { slug }) : "";

    return usePost<ISpaceBookingRequirements>(fetchKey, params, config);
  },

  useSingleSpacePeriodStatus: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    return useFetch<{ status: TActivityStatus }>(
      slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSingleStatus, { slug }) : null,
      params,
      config
    );
  },

  useSpacesList: (args?: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch<IPaginatedResponse<ISingleSpaceItem>>(API_ROUTES.spacesUrls.spaces, params, config);
  },

  useSpacesShortList: (args?: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch<IPaginatedResponse<ISingleSpaceItem>>(API_ROUTES.spacesUrls.spacesShort, params, config);
  },

  useSlots: (args?: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch<IPaginatedResponse<ISlot>>(API_ROUTES.slots, params, config);
  },
  useSpaceActivity: (args?: TSpaceActivityQuery) => {
    const { slug, activityId, params, config } = args || {};

    return useFetch(
      (slug && activityId) ? pathToUrl(API_ROUTES.spacesUrls.spaceActivity, { slug, activityId }) : null, params, config
    );
  },

  useUpdateSpaceActivity: (args: TSpaceActivityQuery) => {
    const { slug, activityId, params, config } = args || {};

    return usePatch(
      (slug && activityId) ? pathToUrl(API_ROUTES.spacesUrls.spaceActivity, { slug, activityId }) : null, params, config
    );
  },

  usePostSpaceCalendar: (args: IUseMutation) => {
    const { params, config } = args || {};

    return usePost(API_ROUTES.spacesUrls.spaceCalendarAll, params, config);
  },

  useUpdateCalendarRecord: ({ slug, recordId, params, config }: IUseMutation & { recordId: string | number }) => {
    const fetchKey = slug && recordId ? pathToUrl(API_ROUTES.spacesUrls.spaceCalendarRecord, { slug, recordId }) : null;

    return usePatch(fetchKey, params, config);
  },

  useDeleteCalendarRecord: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceCalendar, { slug }) : null;

    return useDelete(fetchKey, params, config);
  },

  useCloneSpace: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceClone, { slug }) : null;
    
    return usePost(fetchKey, params, config);
  },

  useDeleteSpace: (args?: IUseMutation) => {
    const { params, config } = args || {};

    return useDelete(API_ROUTES.spacesUrls.spaces, params, config);
  },

  usePublishSpace: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spacePublish, { slug }) : null;

    return usePut(fetchKey, params, config);
  },

  useUnpublishSpace: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceUnpublish, { slug }) : null;

    return usePut(fetchKey, params, config);
  },

  useRestoreSpace: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceRestore, { slug }) : null;

    return usePut(fetchKey, params, config);
  },

  usePostSpace: (args?: IUseMutation<ISpaceFullResponse>) => {
    const { params, config } = args || {};

    return usePost<ISpaceFullResponse>(API_ROUTES.spacesUrls.spaces, params, config);
  },

  usePatchSpace: (args?: IUseMutation<ISpaceFullResponse>) => {
    const { slug, params, config } = args || {};

    return usePatch<ISpaceFullResponse>(
      slug
        ? pathToUrl(API_ROUTES.spacesUrls.spaceSingle, { slug })
        : null,
      params,
      config
    );
  },

  useArchiveSpace: ({ slug, params, config }: IUseMutation) => {
    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceArchive, { slug }) : null;

    return usePut(fetchKey, params, config);
  },

  useSpacesAvailableMonth: (args?: QueryBySlugArgs) => {
    const { params, config, slug } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceAvailableMonth, { slug }) : null;
    
    return useFetch<ISpaceAvailableMonth>(fetchKey, params, config);
  },

  useUpdateSpacesAvailability: ({ params, config }: IUseMutation) => {
    return usePatch(API_ROUTES.spacesUrls.spacesUpdateAvailability, params, config);
  },

  useUpdateSpacesPosition: ({ params, config }: IUseMutation) => {

    return usePatch(API_ROUTES.spacesUrls.spacesChangePosition, params, config);
  },

  useSpacesCountByStatusRequest: (args?: QueryBySlugArgs) => {
    const { params, config } = args || {};

    return useFetch(API_ROUTES.spacesUrls.spacesCountByStatus, params, config);
  },

  usePostSpaceFirstStep: (args?: IUseMutation) => {
    const { params } = args || {};

    return usePost(API_ROUTES.spacesUrls.spaceCreateByFirstStep, params);
  },

  useUpdateSpaceFirstStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFirstStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceFirstStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFirstStep, { slug }) : null;

    return useFetch<ISpaceFirstStepResponse>(fetchKey, params, config);
  },

  useUpdateSpaceSecondStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSecondStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceSecondStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSecondStep, { slug }) : null;

    return useFetch<ISpaceSecondStepResponse>(fetchKey, params, config);
  },

  useUpdateSpaceThirdStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceThirdStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceThirdStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceThirdStep, { slug }) : null;

    return useFetch<ISpaceThirdStepResponse>(fetchKey, params, config);
  },

  useUpdateSpaceFourthStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFourthStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceFourthStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFourthStep, { slug }) : null;

    return useFetch<ISpaceFourthStepResponse>(fetchKey, params, config);
  },

  useUpdateSpaceFifthStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFifthStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceFifthStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceFifthStep, { slug }) : null;

    return useFetch<ISpaceFifthStepResponse>(fetchKey, params, config);
  },

  useUpdateSpaceSixthStep: (args?: IUseMutation) => {
    const { slug, params } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSixthStep, { slug }) : null;

    return usePatch(fetchKey, params);
  },

  useGetSpaceSixthStep: (args?: QueryBySlugArgs) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceSixthStep, { slug }) : null;

    return useFetch<ISpaceSixthStepResponse>(fetchKey, params, config);
  },

  usePatchSpaceCancelReview: (args?: IUseMutation<ISpaceFullResponse>) => {
    const { slug, params, config } = args || {};

    const fetchKey = slug ? pathToUrl(API_ROUTES.spacesUrls.spaceCancelReview, { slug }) : null;

    return usePatch<ISpaceFullResponse>(fetchKey, params, config);
  },
};

export default spaces;
